// RTK Query slice for the /api/v1/identify/company endpoint.
//
// Resolves fuzzy company references (name, website, LinkedIn URL,
// crunchbase URL, Crustdata company_id) to a canonical Crustdata
// company record. Backed by the FREE /screener/identify endpoint
// upstream — no credit charge on the portal side.

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export interface IdentifyCompanyRequest {
  name?: string;
  website?: string;
  linkedin_url?: string;
  crunchbase_url?: string;
  company_id?: number;
  /** When true (default) Crustdata only returns unambiguous matches. */
  exact_match?: boolean;
  /** Max canonical matches to return (1-10). */
  count?: number;
}

/**
 * Subset of the canonical Crustdata company record we actually read.
 * The full record has 60+ fields — the portal only reads what it uses
 * to render a chip + power downstream filter substitution.
 */
export interface IdentifiedCompany {
  company_id: number;
  company_name: string;
  linkedin_profile_url?: string;
  company_website_domain?: string;
  hq_country?: string;
  hq_location?: string;
  linkedin_industries?: string[];
  employee_count_range?: string;
  employee_metrics?: { latest_count?: number };
  last_funding_round_type?: string;
  crunchbase_categories?: string[];
  logo_permalink?: string;
}

export interface IdentifyCompanyResponse {
  companies: IdentifiedCompany[];
  is_full_domain_match?: boolean;
}

export const identifyApi = createApi({
  reducerPath: 'identifyApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    identifyCompany: builder.mutation<
      IdentifyCompanyResponse,
      IdentifyCompanyRequest
    >({
      query: (payload) => ({
        url: '/identify/company',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const { useIdentifyCompanyMutation } = identifyApi;
